import { useState, ReactElement } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Edit,
  Delete,
  MoreVert,
  Payment,
  CheckCircle,
  Cancel,
  Business,
  Settings,
  Add,
  Close,
  PhoneAndroid,
  CreditCard,
  AccountBalance,
  CompareArrows,
} from '@mui/icons-material';
import { usePaymentModules, useDeletePaymentModule, useCreatePaymentModule, useUpdatePaymentModule } from '../../hooks';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useQueryClient } from '@tanstack/react-query';
import { colors, transitions, radius } from '../../theme/theme';

interface ModuleField {
  name: string;
  type: 'text' | 'number' | 'select' | 'checkbox' | 'radio';
  options?: string;
  required?: boolean;
}

const getProviderIcon = (provider: string): ReactElement => {
  const iconMap: Record<string, ReactElement> = {
    mpesa: <PhoneAndroid sx={{ fontSize: 16 }} />,
    airtel_money: <PhoneAndroid sx={{ fontSize: 16 }} />,
    card: <CreditCard sx={{ fontSize: 16 }} />,
    pesalink: <CompareArrows sx={{ fontSize: 16 }} />,
    bank_deposit: <AccountBalance sx={{ fontSize: 16 }} />,
  };
  return iconMap[provider] || <Payment sx={{ fontSize: 16 }} />;
};

export const PaymentModulesPage = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedModule, setSelectedModule] = useState<any>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    provider: string;
    description: string;
    status: string;
    fields: ModuleField[];
  }>({
    name: '',
    provider: '',
    description: '',
    status: 'active',
    fields: [{ name: '', type: 'text', options: '', required: false }],
  });

  const { paymentModules, isLoading, error } = usePaymentModules(page + 1);
  const deletePaymentModule = useDeletePaymentModule();
  const createPaymentModule = useCreatePaymentModule();
  const updatePaymentModule = useUpdatePaymentModule();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, module: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedModule(module);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedModule(null);
  };

  const handleDelete = async () => {
    if (!selectedModule) return;

    if (window.confirm(`Are you sure you want to delete payment module "${selectedModule.name}"?`)) {
      try {
        await deletePaymentModule.mutateAsync(selectedModule.id);
        showSnackbar('Payment module deleted successfully', 'success');
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'payment-modules'] });
        handleMenuClose();
      } catch (error) {
        showSnackbar('Failed to delete payment module', 'error');
      }
    }
  };

  const handleOpenAddDialog = () => {
    setFormData({
      name: '',
      provider: '',
      description: '',
      status: 'active',
      fields: [{ name: '', type: 'text', options: '', required: false }],
    });
    setAddDialogOpen(true);
  };

  const handleOpenEditDialog = () => {
    if (!selectedModule) return;
    setFormData({
      name: selectedModule.name || '',
      provider: selectedModule.provider || '',
      description: selectedModule.description || '',
      status: selectedModule.status || 'active',
      fields: selectedModule.fields && selectedModule.fields.length > 0
        ? selectedModule.fields
        : [{ name: '', type: 'text', options: '', required: false }],
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseDialogs = () => {
    setAddDialogOpen(false);
    setEditDialogOpen(false);
    setFormData({
      name: '',
      provider: '',
      description: '',
      status: 'active',
      fields: [{ name: '', type: 'text', options: '', required: false }],
    });
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddField = () => {
    setFormData((prev) => ({
      ...prev,
      fields: [...prev.fields, { name: '', type: 'text', options: '', required: false }],
    }));
  };

  const handleRemoveField = (index: number) => {
    if (formData.fields.length === 1) return;
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.filter((_, i) => i !== index),
    }));
  };

  const handleFieldChange = (index: number, field: keyof ModuleField, value: any) => {
    setFormData((prev) => ({
      ...prev,
      fields: prev.fields.map((f, i) =>
        i === index ? { ...f, [field]: value } : f
      ),
    }));
  };

  const handleSubmitAdd = async () => {
    try {
      await createPaymentModule.mutateAsync(formData);
      showSnackbar('Payment module created successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'payment-modules'] });
      handleCloseDialogs();
    } catch (error) {
      showSnackbar('Failed to create payment module', 'error');
    }
  };

  const handleSubmitEdit = async () => {
    if (!selectedModule) return;
    try {
      await updatePaymentModule.mutateAsync({ id: selectedModule.id, data: formData });
      showSnackbar('Payment module updated successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'payment-modules'] });
      handleCloseDialogs();
    } catch (error) {
      showSnackbar('Failed to update payment module', 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Module Name',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Payment sx={{ fontSize: 18, color: colors.primary.main }} />
          <Box>
            <Box sx={{ fontWeight: 600 }}>{params.value}</Box>
            <Box sx={{ fontSize: '0.75rem', color: colors.grey[600] }}>
              {params.row.provider || 'No provider'}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'provider',
      headerName: 'Provider',
      width: 170,
      renderCell: (params) => (
        <Chip
          icon={getProviderIcon(params.value)}
          label={params.value ? params.value.replace('_', ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : 'N/A'}
          size="small"
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      field: 'counties_count',
      headerName: 'Counties',
      width: 110,
      valueGetter: (value) => value || 0,
      renderCell: (params) => (
        <Chip
          icon={<Business sx={{ fontSize: 14 }} />}
          label={params.value}
          size="small"
          color="info"
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const isActive = params.value === 'active';
        return (
          <Chip
            icon={isActive ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueGetter: (value) => (value ? new Date(value).toLocaleDateString() : 'N/A'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Payment Modules"
          subtitle="Configure payment gateway integrations"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Configuration' },
            { label: 'Payment Modules' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAddDialog}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                transition: transitions.base,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Payment Module
            </Button>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load payment modules. Please try again.
          </Alert>
        )}

        {/* Summary Cards */}
        {!isLoading && paymentModules && (
          <Grid container spacing={2.5} mb={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.primary,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.primary.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Total Modules</Box>
                    <Payment sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {paymentModules.meta?.total?.toLocaleString() || paymentModules.data?.length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Payment gateways</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.success,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.success.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Active</Box>
                    <CheckCircle sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {paymentModules.data?.filter((m: any) => m.status === 'active').length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Currently active</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.secondary.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Providers</Box>
                    <Settings sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {new Set(paymentModules.data?.map((m: any) => m.provider).filter(Boolean)).size || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Unique providers</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: colors.gradients.info,
                  color: 'white',
                  borderRadius: 3,
                  border: `1px solid ${colors.info.dark}`,
                  boxShadow: colors.shadows.base,
                  transition: transitions.base,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: colors.shadows.vibrant,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Showing</Box>
                    <Business sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {paymentModules.data?.length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>On current page</Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'visible',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: colors.shadows.base,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={paymentModules?.data || []}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{ page, pageSize }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setPageSize(model.pageSize);
                  }}
                  rowCount={paymentModules?.meta?.total || paymentModules?.data?.length || 0}
                  paginationMode={paymentModules?.meta?.total ? 'server' : 'client'}
                  disableRowSelectionOnClick
                  autoHeight
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  sx={{
                    border: 'none',
                    borderRadius: 2,
                    '& .MuiDataGrid-main': {
                      borderRadius: 2,
                    },
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '16px 20px',
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      borderTopLeftRadius: '16px',
                      borderTopRightRadius: '16px',
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[100]}`,
                      fontSize: '0.875rem',
                      py: 1.5,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.grey[700],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-columnHeader:first-of-type': {
                      borderTopLeftRadius: '16px',
                    },
                    '& .MuiDataGrid-columnHeader:last-of-type': {
                      borderTopRightRadius: '16px',
                    },
                    '& .MuiDataGrid-row': {
                      transition: transitions.fast,
                      '&:hover': {
                        backgroundColor: `${colors.primary.main}08`,
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                      borderBottomLeftRadius: '16px',
                      borderBottomRightRadius: '16px',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleOpenEditDialog}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Module</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: colors.error.main }}>
            <ListItemIcon>
              <Delete fontSize="small" sx={{ color: colors.error.main }} />
            </ListItemIcon>
            <ListItemText>Delete Module</ListItemText>
          </MenuItem>
        </Menu>

        {/* Add/Edit Dialog */}
        <Dialog
          open={addDialogOpen || editDialogOpen}
          onClose={handleCloseDialogs}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>{editDialogOpen ? 'Edit Payment Module' : 'Add Payment Module'}</span>
              <IconButton onClick={handleCloseDialogs} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                label="Module Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                fullWidth
                required
              />
              <FormControl fullWidth required>
                <InputLabel>Payment Provider</InputLabel>
                <Select
                  value={formData.provider}
                  label="Payment Provider"
                  onChange={(e) => handleFormChange('provider', e.target.value)}
                  renderValue={(value) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getProviderIcon(value)}
                      <span>{value ? value.replace('_', ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : ''}</span>
                    </Box>
                  )}
                >
                  <MenuItem value="mpesa">
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PhoneAndroid sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText>M-Pesa</ListItemText>
                  </MenuItem>
                  <MenuItem value="airtel_money">
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PhoneAndroid sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText>Airtel Money</ListItemText>
                  </MenuItem>
                  <MenuItem value="card">
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CreditCard sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText>Card</ListItemText>
                  </MenuItem>
                  <MenuItem value="pesalink">
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CompareArrows sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText>Pesalink</ListItemText>
                  </MenuItem>
                  <MenuItem value="bank_deposit">
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <AccountBalance sx={{ fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText>Bank Deposit</ListItemText>
                  </MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                fullWidth
                multiline
                rows={3}
                required
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleFormChange('status', e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ fontSize: '1rem', fontWeight: 600, color: colors.grey[700] }}>
                    Configuration Fields
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleAddField}
                    sx={{ borderRadius: 2 }}
                  >
                    Add Field
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {formData.fields.map((field, index) => (
                    <Paper
                      key={index}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: `1px solid ${colors.grey[300]}`,
                        borderRadius: 2,
                        position: 'relative',
                        backgroundColor: colors.grey[50],
                      }}
                    >
                      {formData.fields.length > 1 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveField(index)}
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            color: colors.error.main,
                          }}
                        >
                          <Close />
                        </IconButton>
                      )}
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Field Name"
                            value={field.name}
                            onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                            fullWidth
                            size="small"
                            required
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth size="small" required>
                            <InputLabel>Field Type</InputLabel>
                            <Select
                              value={field.type}
                              label="Field Type"
                              onChange={(e) => handleFieldChange(index, 'type', e.target.value)}
                            >
                              <MenuItem value="text">Text</MenuItem>
                              <MenuItem value="number">Number</MenuItem>
                              <MenuItem value="select">Select</MenuItem>
                              <MenuItem value="checkbox">Checkbox</MenuItem>
                              <MenuItem value="radio">Radio</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {(field.type === 'select' || field.type === 'radio') && (
                          <Grid size={{ xs: 12 }}>
                            <TextField
                              label="Options (comma-separated)"
                              value={field.options}
                              onChange={(e) => handleFieldChange(index, 'options', e.target.value)}
                              fullWidth
                              size="small"
                              placeholder="Option1,Option2,Option3"
                              helperText="Separate options with commas"
                            />
                          </Grid>
                        )}
                        <Grid size={{ xs: 12 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.required}
                                onChange={(e) => handleFieldChange(index, 'required', e.target.checked)}
                                size="small"
                              />
                            }
                            label="Required Field"
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={handleCloseDialogs} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={editDialogOpen ? handleSubmitEdit : handleSubmitAdd}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                transition: transitions.base,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                },
              }}
            >
              {editDialogOpen ? 'Update Module' : 'Create Module'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
